import { inject, Injectable } from '@angular/core';
import {
    FormFields,
    IndividualField,
    ReferenceFieldArray,
    CodeableConceptField,
    CodeField,
    IndividualReferenceField,
    GroupField
} from '../shared/dynamic-forms.interface2';
import { Dosage } from 'fhir/r4';
import { UtilityService } from '../shared/utility.service';

@Injectable({ providedIn: 'root' })
export class MedicationDispenseFieldFilterService {
    utilityService = inject(UtilityService);
    filterFormFields(fields: FormFields[], allowedFieldApiNames: string[]): FormFields[] {
        if (!fields || !allowedFieldApiNames?.length) {
            return [];
        }
        const allowedSet = new Set(allowedFieldApiNames);
        return fields.filter((field) => allowedSet.has(field.generalProperties.fieldApiName));
    }

    removeFormFields(fields: FormFields[], excludedFieldApiNames: string[]): FormFields[] {
        if (!fields || !excludedFieldApiNames?.length) {
            return fields;
        }
        const excludedSet = new Set(excludedFieldApiNames);
        for (let i = fields.length - 1; i >= 0; i--) {
            if (excludedSet.has(fields[i].generalProperties.fieldApiName)) {
                fields.splice(i, 1);
            }
        }
        return fields;
    }
    flattenDosageInstructionKeys(dosage: Dosage[]): string[] {
        // Display keys only (no dots). Each key represents a consolidated view.
        const keys: string[] = [];
        dosage.forEach((_, i) => {
            const n = i + 1;
            keys.push(`Dosage ${n} - Instruction`);
            keys.push(`Dosage ${n} - Timing`);
            keys.push(`Dosage ${n} - Timing Code`);
            keys.push(`Dosage ${n} - Duration`);
            keys.push(`Dosage ${n} - Dose`);
        });
        return keys;
    }

    flattenDosageInstructionValues(dosage: Dosage[]): Record<string, any> {
        const out: Record<string, any> = {};

        const clean = (v: any) => (v === undefined || v === null ? '' : String(v).trim());

        const fmtUnit = (unit: any): string => {
            const u = clean(unit);
            if (!u) return '';
            // Prefer UtilityService durationUnits mapping (code -> display)
            return this.utilityService.durationUnitCodeToDisplay(u) ?? u;
        };

        const fmtTiming = (d: any): string => {
            const repeat = d?.timing?.repeat ?? {};
            const freq = repeat?.frequency;
            const period = repeat?.period;
            const periodUnit = repeat?.periodUnit;

            if (freq != null && period != null) {
                const pu = fmtUnit(periodUnit);
                return `${freq} time(s) every ${period}${pu ? ` ${pu}` : ''}`.trim();
            }

            return '';
        };

        const fmtTimingCodeOnly = (d: any): string => {
            return (
                clean(d?.timing?.code?.text) ||
                clean(d?.timing?.code?.coding?.[0]?.display) ||
                clean(d?.timing?.code?.coding?.[0]?.code)
            );
        };

        const fmtDuration = (d: any): string => {
            const repeat = d?.timing?.repeat ?? {};
            const duration = repeat?.duration;
            const durationUnit = repeat?.durationUnit;

            const u = fmtUnit(durationUnit);
            if (duration == null && !u) return '';
            return `${clean(duration)} ${u}`.trim();
        };

        const fmtDose = (d: any): string => {
            const dq = d?.doseAndRate?.[0]?.doseQuantity;
            const value = dq?.value;
            const unit = dq?.unit || dq?.code;
            if (value == null && !unit) return '';
            return `${clean(value)} ${clean(unit)}`.trim();
        };

        dosage.forEach((d: any, i) => {
            const n = i + 1;
            const instruction = clean(d?.text);
            const timing = fmtTiming(d);
            const timingCode = fmtTimingCodeOnly(d);
            const duration = fmtDuration(d);
            const dose = fmtDose(d);

            if (instruction) out[`Dosage ${n} - Instruction`] = instruction;
            if (timing) out[`Dosage ${n} - Timing`] = timing;
            if (timingCode) out[`Dosage ${n} - Timing Code`] = timingCode;
            if (duration) out[`Dosage ${n} - Duration`] = duration;
            if (dose) out[`Dosage ${n} - Dose`] = dose;
        });

        return out;
    }
}
